import { useState, useEffect } from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { Optimizely } from '@grandvisionhq/api-client';

function trackExperiment(userId, experiment, variation) {
    DataLayer.pushEvent({ event: '', atfEcommerce: null });
    DataLayer.pushEvent({
        event: 'experiment_init',
        session: {
            experiments: {
                platform: 'optimizely_sdk',
                experimentId: experiment,
                variationId: variation,
            },
        },
        user: {
            id: {
                optimizely_sdk: userId,
            },
        },
    });
}

var EXPERIMENT_STORAGE_KEY = 'disabled.ab.tests';
function experimentSuccess(variation) {
    return { variation: variation, error: null, isLoading: false };
}
function experimentError(error) {
    return { variation: null, error: error, isLoading: false };
}
function experimentLoading() {
    return { variation: null, error: null, isLoading: true };
}
/**
 * We want to prevent A/B testing from running on nightly tests.
 * The tests script will inject a key to localStorage! If the value for the key is "true",
 * we want to disable the A/B testing.
 */
function shouldDisableExperiments() {
    if (typeof window === 'undefined') {
        return true;
    }
    return localStorage.getItem(EXPERIMENT_STORAGE_KEY) === 'true';
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}
function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

// A userId to Experiment mapping
var experimentCache = {};
/**
 * usePrepareExperiment hook should be used for pre-caching the experiment result
 * e.g. consider a case where A/B testing would result in using different orders, colors, and texts,
 * when the component receives the variation after first render, it might cause a flash in the component.
 * In cases like these we might want to fetch the variation before the first render of the component.
 *
 * This hook will NOT push the GA event (use `useExperiment`)
 */
function usePrepareExperiment(experiment, userId) {
    var _a = __read(useState(function () {
        var _a;
        if (!userId) {
            return experimentError(new Error('ERR_USER_ID'));
        }
        var cache = (_a = experimentCache[userId]) === null || _a === void 0 ? void 0 : _a[experiment];
        if (cache) {
            return cache;
        }
        return experimentLoading();
    }), 2), status = _a[0], setStatus = _a[1];
    function updateStatus(userId, result) {
        // updating the cache
        if (!experimentCache[userId]) {
            experimentCache[userId] = {};
        }
        experimentCache[userId][experiment] = result;
        // updating internal state
        setStatus(result);
    }
    function fetchExperiment(userId) {
        return __awaiter(this, void 0, void 0, function () {
            var apiUrl, variation, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        updateStatus(userId, experimentLoading());
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        apiUrl = window.location.origin;
                        return [4 /*yield*/, Optimizely.decide(apiUrl, { userId: userId, experiment: experiment })];
                    case 2:
                        variation = (_a.sent()).variation;
                        updateStatus(userId, experimentSuccess(variation));
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        updateStatus(userId, experimentError(error_1));
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    }
    useEffect(function () {
        var _a;
        if (userId) {
            // check if experiment should be disabled
            if (shouldDisableExperiments()) {
                return updateStatus(userId, experimentError(new Error('ERR_DISABLED')));
            }
            var state = (_a = experimentCache[userId]) === null || _a === void 0 ? void 0 : _a[experiment];
            if (state && !state.isLoading) {
                setStatus(state);
            }
            else {
                fetchExperiment(userId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, experiment]);
    return status;
}

/**
 * useExperiment hook will return the experiment variation!
 * This hook will also push the GA event
 *
 * If you need to pre-fetch the result before first render of the component,
 *  use `usePrepareExperiment` somewhere in parent components tree
 */
function useExperiment(experiment, userId) {
    var result = usePrepareExperiment(experiment, userId);
    useEffect(function () {
        if (shouldDisableExperiments()) {
            return;
        }
        if (!result.isLoading && userId) {
            if (result.error) {
                // check if experiment has been deactivated
                if (result.error.message !== 'ACTIVATION_FAILED') {
                    trackExperiment(userId, experiment, 'error');
                }
            }
            else {
                trackExperiment(userId, experiment, result.variation);
            }
        }
    }, [userId, experiment, result]);
    return result;
}

export { useExperiment, usePrepareExperiment };
//# sourceMappingURL=index.js.map
